import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  McNotification,
  McLoadingIndicator,
  McButton,
  McIcon,
} from '@maersk-global/mds-react-wrapper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryService } from '../services/queryService';
import { agentService, PromptTemplateDTO } from '../services/agentService';
import { useNotification } from '../context/NotificationContext';
import { sanitizeInput, validateQueryId, validateSQL } from '../utils/validators';
import { extractParametersFromSql } from '../utils/queryUtils';
import { QueryDefinition, ParameterDefinition, ValidationResponse } from '../types';
import { InfoStep } from '../components/edit-query/steps/InfoStep';
import { SqlStep } from '../components/edit-query/steps/SqlStep';
import type { SqlEditorHandle } from '../components/Common/SqlEditor';
import { SchemaExplorer } from '../components/SchemaExplorer';
import { useSchemaPanelState } from '../hooks/useSchemaPanelState';
import { applyLeadingSpace } from '../utils/schemaInsertion';
import { ConfirmationDialog } from '../components/Common/ConfirmationDialog';
import { SqlDiffDialog } from '../components/Common/SqlDiffDialog';
import FloatingAssistant from '../components/FloatingAssistant';
import { ChatMessage, resolveAiModelOptions, resolveAgentErrorMessage } from './CreateQueryPage';
import { classifySqlError } from '../utils/sqlErrorClassifier';

interface TestParameter {
  name: string;
  type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'TIMESTAMP' | 'STRING_LIST';
  value: string;
  description?: string;
}



const QueryEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { queryId } = useParams<{ queryId: string }>();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // Stepper State
  const [activeStep, setActiveStep] = useState(0);

  // Schema explorer state
  const sqlEditorRef = useRef<SqlEditorHandle>(null);
  const { open: schemaPanelOpen, setOpen: setSchemaPanelOpen, toggle: toggleSchemaPanel } = useSchemaPanelState('edit');
  const [showSchemaExplorerFull, setShowSchemaExplorerFull] = useState(false);
  const [schemaPanelTab, setSchemaPanelTab] = useState<'schema' | 'field-search' | 'field-catalog'>('schema');
  const [schemaExplorerWarning, setSchemaExplorerWarning] = useState<string | null>(null);
  const testTerminalRef = useRef<string>('');
  const openSchemaPanelTab = useCallback((tab: 'schema' | 'field-search' | 'field-catalog') => {
    if (!testTerminalRef.current) {
      setSchemaExplorerWarning('Please select a terminal first to open Schema Explorer.');
      setSchemaPanelOpen(false);
      return;
    }
    setSchemaExplorerWarning(null);
    if (schemaPanelOpen && schemaPanelTab === tab) {
      setSchemaPanelOpen(false);
      return;
    }
    setSchemaPanelTab(tab);
    setSchemaPanelOpen(true);
  }, [schemaPanelOpen, schemaPanelTab, setSchemaPanelOpen]);

  // Form state
  const [formData, setFormData] = useState({
    queryId: queryId || '',
    name: '',
    description: '',
    category: '',
    applicationId: '',
    sqlContent: '',
    notes: '',
    makeCurrent: true,
  });

  const [tags, setTags] = useState<string[]>([]);
  const [parameters, setParameters] = useState<ParameterDefinition[]>([]);
  const [currentTag, setCurrentTag] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [testTerminal, setTestTerminal] = useState<string>('');
  const [databaseType, setDatabaseType] = useState<'ORACLE' | 'POSTGRESQL' | 'EXTERNAL'>('ORACLE');
  const [connectionId, setConnectionId] = useState<string>('');
  const [testParameters, setTestParameters] = useState<TestParameter[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showOverrideConfirmDialog, setShowOverrideConfirmDialog] = useState(false);
  const [overrideMakeCurrent, setOverrideMakeCurrent] = useState(true);
  const [testResults, setTestResults] = useState<any>(null);
  
  // Explain Plan State
  const [isExplainingPlan, setIsExplainingPlan] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [isGeneratingAiExplanation, setIsGeneratingAiExplanation] = useState(false);

  // AI Assistant State
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const chatHistoryRef = useRef<ChatMessage[]>([]);
  const [aiIntent, setAiIntent] = useState('');
  const aiIntentRef = useRef(aiIntent);
  const [aiModelName, setAiModelName] = useState(() => {
    const options = resolveAiModelOptions();
    return options[0]?.value ?? 'gpt-4o-mini';
  });
  const aiModelRef = useRef(aiModelName);
  const aiModelOptions = React.useMemo(() => resolveAiModelOptions(), []);
  const [aiDomainOverride, setAiDomainOverride] = useState('AUTO');
  const aiDomainRef = useRef(aiDomainOverride);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Auto-fix state
  const [autoFixModel, setAutoFixModel] = useState(() => {
    const options = resolveAiModelOptions();
    return options[0]?.value ?? 'gpt-4o-mini';
  });
  const autoFixModelRef = useRef(autoFixModel);

  // AI correction diff state
  const [aiCorrectionDiff, setAiCorrectionDiff] = useState<{ original: string; corrected: string } | null>(null);
  const [showDiffDialog, setShowDiffDialog] = useState(false);

  // Terminal override state
  const [terminalOverrideSql, setTerminalOverrideSql] = useState<string | null>(null);
  const [isOverrideActive, setIsOverrideActive] = useState(false);
  const baseSqlRef = useRef<string>('');

  useEffect(() => { chatHistoryRef.current = chatHistory; }, [chatHistory]);
  useEffect(() => { aiIntentRef.current = aiIntent; }, [aiIntent]);
  useEffect(() => { aiDomainRef.current = aiDomainOverride; }, [aiDomainOverride]);
  useEffect(() => { aiModelRef.current = aiModelName; }, [aiModelName]);
  useEffect(() => { testTerminalRef.current = testTerminal; }, [testTerminal]);
  useEffect(() => { autoFixModelRef.current = autoFixModel; }, [autoFixModel]);

  useEffect(() => {
    if (!schemaPanelOpen) return;
    if (!testTerminal) setSchemaPanelOpen(false);
  }, [schemaPanelOpen, testTerminal, setSchemaPanelOpen]);

  // When the test terminal changes, check for a terminal override
  useEffect(() => {
    if (!queryId || !testTerminal) {
      setTerminalOverrideSql(null);
      setIsOverrideActive(false);
      return;
    }
    queryService.getTerminalOverride(queryId, testTerminal).then(override => {
      if (override) {
        baseSqlRef.current = formDataRef.current.sqlContent;
        setTerminalOverrideSql(override.sqlContent);
        setIsOverrideActive(true);
      } else {
        setTerminalOverrideSql(null);
        setIsOverrideActive(false);
      }
    }).catch(() => {
      setTerminalOverrideSql(null);
      setIsOverrideActive(false);
    });
  }, [testTerminal, queryId]);

  // AI Domain options
  const { data: aiTemplates } = useQuery<PromptTemplateDTO[]>({
    queryKey: ['promptTemplates', databaseType],
    queryFn: () => agentService.getPromptTemplates(databaseType),
    staleTime: 1000 * 60 * 5,
  });

  const aiDomainOptions = React.useMemo(() => {
    const opts = [{ label: 'Auto-Select (Supervisor)', value: 'AUTO' }];
    if (aiTemplates && aiTemplates.length > 0) {
      aiTemplates.forEach(t => opts.push({ label: `Expert Agent: ${t.name}`, value: t.templateId! }));
    }
    return opts;
  }, [aiTemplates]);

  // Update test parameters when SQL changes
  useEffect(() => {
    const extracted = extractParametersFromSql(formData.sqlContent || '');
    const newTestParams = extracted.map((newParam) => {
      const existing = testParametersRef.current.find((p) => p.name === newParam.name);
      const definedParam = parametersRef.current.find(p => p.name === newParam.name);

      let type = newParam.type;
      if (existing) type = existing.type;
      else if (definedParam) type = definedParam.type as any;

      let value = newParam.value;
      if (existing) value = existing.value;
      else if (definedParam && definedParam.defaultValue) value = definedParam.defaultValue;

      let description = '';
      if (existing && existing.description) description = existing.description;
      else if (definedParam) description = definedParam.description || '';

      return { ...newParam, type, value, description };
    });
    setTestParameters(newTestParams);
    testParametersRef.current = newTestParams;
  }, [formData.sqlContent, parameters]);

  // Fetch existing query data
  const { data: queryData, isLoading: isLoadingQuery, error: queryError } = useQuery({
    queryKey: ['query', queryId],
    queryFn: () => queryService.getQuery(queryId!),
    enabled: !!queryId,
  });

  // Fetch applications for selection
  const { data: applications = [] } = useQuery({
    queryKey: ['applications'],
    queryFn: () => queryService.getApplications(),
  });

  // Load query data into form
  useEffect(() => {
    if (queryData) {
      const newFormData = {
        queryId: queryData.queryId || '',
        name: queryData.name || '',
        description: queryData.description || '',
        category: queryData.category || '',
        applicationId: queryData.applicationId || '',
        sqlContent: queryData.sqlContent || '',
        notes: queryData.metadata?.notes || '',
        makeCurrent: true,
      };
      setFormData(newFormData);
      formDataRef.current = newFormData;

      setTags(queryData.tags || []);
      tagsRef.current = queryData.tags || [];

      setParameters(queryData.parameters || []);
      parametersRef.current = queryData.parameters || [];
    }
  }, [queryData]);

  // Mutations
  const updateQueryMutation = useMutation({
    mutationFn: (data: Partial<QueryDefinition>) => queryService.updateQuery(queryId!, data),
    onSuccess: () => {
      showNotification('Query updated successfully!', 'success');
    },
    onError: (error: any) => {
      console.error('Error updating query:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update query';
      showNotification(`Error updating query: ${errorMessage}`, 'error');
    },
  });

  const saveOverrideMutation = useMutation({
    mutationFn: (makeCurrent: boolean) => queryService.saveTerminalOverride(queryId!, testTerminal, {
      sqlContent: isOverrideActive && terminalOverrideSql !== null
        ? terminalOverrideSql
        : formDataRef.current.sqlContent,
      makeCurrent,
    }),
    onSuccess: async (saved) => {
      showNotification(`Override saved for ${testTerminal}`, 'success');
      baseSqlRef.current = formDataRef.current.sqlContent;
      setTerminalOverrideSql(saved.sqlContent);
      setIsOverrideActive(true);
      // Invalidate so QueryDetailPage (and any cached data) reflects the new override immediately
      await queryClient.invalidateQueries({ queryKey: ['terminalOverrides', queryId], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['overrideVersions', queryId, testTerminal], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['query', queryId], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['query-versions', queryId], refetchType: 'all' });
      navigate(`/queries/${queryId}`);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Failed to save override';
      showNotification(`Error saving override: ${msg}`, 'error');
    },
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: () => queryService.deleteTerminalOverride(queryId!, testTerminal),
    onSuccess: () => {
      showNotification(`Override removed for ${testTerminal}`, 'success');
      setTerminalOverrideSql(null);
      setIsOverrideActive(false);
      // Invalidate so QueryDetailPage reflects the removal immediately
      queryClient.invalidateQueries({ queryKey: ['terminalOverrides', queryId] });
      queryClient.invalidateQueries({ queryKey: ['overrideVersions', queryId, testTerminal] });
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Failed to remove override';
      showNotification(`Error removing override: ${msg}`, 'error');
    },
  });

  const validateQueryMutation = useMutation({
    mutationFn: ({ sqlContent, parameters }: { sqlContent: string; parameters?: Record<string, any> }) =>
      queryService.validateQuery({
        sqlContent,
        terminalId: testTerminal,
        databaseType: databaseType,
        connectionId: databaseType === 'EXTERNAL' ? connectionId : undefined,
        parameters,
      }),
    onSuccess: (data: ValidationResponse) => {
      setValidationResult(data);
    },
    onError: (error: any) => {
      console.error('Validation error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Validation failed';
      showNotification(`Validation error: ${errorMessage}`, 'error');
    },
  });

  const handleAiExplainPlan = async () => {
    if (!validationResult?.planLines || isGeneratingAiExplanation) return;

    setIsGeneratingAiExplanation(true);
    try {
      const explanation = await queryService.explainPlanWithAi(
        formDataRef.current.sqlContent,
        validationResult.planLines,
        aiModelRef.current
      );
      setAiExplanation(explanation);
    } catch (error: any) {
      showNotification(`AI explanation failed: ${error.message}`, 'error');
    } finally {
      setIsGeneratingAiExplanation(false);
    }
  };

  // AI Generate mutation
  const aiGenerateMutation = useMutation({
    mutationFn: (data: any) => agentService.generateQuery(data),
    onSuccess: (response: any) => {
      if (response.requiresClarification) {
        setChatHistory(prev => [...prev, { role: 'assistant', content: response.clarificationMessage }]);
      } else {
        let generatedSql = response.sqlQuery ?? response.generatedSql ?? '';
        if (generatedSql.trim().endsWith(';')) {
          generatedSql = generatedSql.trim().slice(0, -1);
        }
        const explanation = response.explanation || 'Generated successfully.';
        setChatHistory(prev => [...prev, { role: 'assistant', content: `Here is the SQL:\n\n\`\`\`sql\n${generatedSql}\n\`\`\`\n\n${explanation}` }]);

        const firstUserIntent = chatHistoryRef.current.find(m => m.role === 'user')?.content || aiIntentRef.current.trim() || 'Autogenerated Query';
        const formattedExplanation = explanation.split('\n').map((line: string) => `-- ${line}`).join('\n');
        const finalSql = `-- Goal: ${firstUserIntent.replace(/\n/g, ' ')}\n--\n${formattedExplanation}\n\n${generatedSql}`;
        handleInputChange('sqlContent', finalSql);
        showNotification('SQL generated successfully! Review and edit below.', 'success');
      }
    },
    onError: (error: any) => {
      const resolvedMessage = resolveAgentErrorMessage(error);
      if (error?.response?.status !== 429) {
        showNotification(`AI generation failed: ${resolvedMessage}`, 'error');
      }
      setChatHistory(prev => [...prev, { role: 'assistant', content: `Error: ${resolvedMessage}` }]);
    },
  });

  const handleAiGenerate = () => {
    const intent = aiIntentRef.current.trim();
    if (!intent) {
      showNotification('Please enter a message', 'warning');
      return;
    }
    setChatHistory(prev => [...prev, { role: 'user', content: intent }]);
    setAiIntent('');
    aiGenerateMutation.mutate({
      naturalLanguageQuery: intent,
      terminalId: testTerminalRef.current,
      databaseType: databaseType,
      pageContext: {
        sqlContent: formDataRef.current.sqlContent,
        queryName: formDataRef.current.name,
        description: formDataRef.current.description,
        category: formDataRef.current.category,
      },
      options: {
        domainOverride: aiDomainRef.current,
        modelName: aiModelRef.current,
        useLiveSchema: false,
        detailedComments: true,
      },
      conversationHistory: chatHistory,
    });
  };

  const handleAiRetryMessage = (message: string) => {
    const retryIntent = (message || '').trim();
    if (!retryIntent || aiGenerateMutation.isPending) return;
    setChatHistory(prev => [...prev, { role: 'user', content: retryIntent }]);
    setAiIntent('');
    aiGenerateMutation.mutate({
      naturalLanguageQuery: retryIntent,
      terminalId: testTerminalRef.current,
      databaseType: databaseType,
      pageContext: {
        sqlContent: formDataRef.current.sqlContent,
        queryName: formDataRef.current.name,
        description: formDataRef.current.description,
        category: formDataRef.current.category,
      },
      options: {
        domainOverride: aiDomainRef.current,
        modelName: aiModelRef.current,
        useLiveSchema: false,
        detailedComments: true,
      },
      conversationHistory: chatHistoryRef.current,
    });
  };

  // --- Auto-fix SQL mutation ---
  const autoFixSqlMutation = useMutation({
    mutationFn: (data: any) => agentService.generateQuery(data),
    onSuccess: (response: any) => {
      if (response?.requiresClarification && response?.clarificationMessage) {
        showNotification(`AI Query Correction needs more info: ${response.clarificationMessage}`, 'warning');
        return;
      }
      let generatedSql = '';
      const candidates = [response?.sqlQuery, response?.generatedSql];
      for (const candidate of candidates) {
        if (typeof candidate !== 'string') continue;
        const fenced = candidate.match(/```(?:sql)?\s*\n?([\s\S]*?)```/i);
        const extracted = fenced ? fenced[1].trim() : candidate.trim();
        if (extracted) { generatedSql = extracted; break; }
      }
      if (generatedSql.endsWith(';')) generatedSql = generatedSql.slice(0, -1);
      if (!generatedSql.trim()) {
        showNotification('AI Query Correction did not return a corrected SQL query.', 'warning');
        return;
      }
      const originalSql = formDataRef.current.sqlContent?.trim() || '';
      setFormData(prev => ({ ...prev, sqlContent: generatedSql }));
      formDataRef.current = { ...formDataRef.current, sqlContent: generatedSql };
      setAiCorrectionDiff({ original: originalSql, corrected: generatedSql });
      setValidationResult(null);
      setTestResults(null);
      showNotification('SQL corrected by AI. Review and execute again.', 'success');
    },
    onError: (error: any) => {
      const resolvedMessage = resolveAgentErrorMessage(error);
      showNotification(`Auto-correction failed: ${resolvedMessage}`, 'error');
    },
  });

  const buildSqlErrorContext = () => {
    const details: string[] = [];
    if ((validationResult as any)?.error) details.push(String((validationResult as any).error));
    if (validationResult?.validationMessage && !validationResult?.isValid) details.push(String(validationResult.validationMessage));
    if (Array.isArray((validationResult as any)?.syntaxErrors)) details.push(...(validationResult as any).syntaxErrors);
    if (Array.isArray((validationResult as any)?.errors)) details.push(...(validationResult as any).errors);
    if (Array.isArray((validationResult as any)?.databaseValidationMessages)) details.push(...(validationResult as any).databaseValidationMessages);
    if (Array.isArray(validationResult?.vulnerabilities)) {
      details.push(...validationResult!.vulnerabilities!.map((v: any) => String(v?.description || '')));
    }
    if (testResults?.errorMessage) details.push(String(testResults.errorMessage));
    if (testResults?.error) details.push(String(testResults.error));
    const merged = Array.from(new Set(details.map((d: string) => d?.trim()).filter(Boolean))).join('\n');
    return merged || 'SQL execution/validation failed.';
  };

  const handleAutoFixSql = () => {
    const currentSql = formDataRef.current.sqlContent?.trim();
    if (!currentSql) {
      showNotification('SQL content is empty. Nothing to fix.', 'warning');
      return;
    }
    const errorContext = buildSqlErrorContext();
    const classification = classifySqlError(errorContext);
    if (!classification.fixable) {
      showNotification(classification.reason || 'This error cannot be fixed by rewriting SQL.', 'warning');
      return;
    }
    autoFixSqlMutation.mutate({
      naturalLanguageQuery: [
        'Fix this SQL query based on the database validation/execution error.',
        'Return ONLY the corrected executable SQL inside a ```sql code block.',
        'Do NOT ask clarifying questions. Do NOT include explanations outside the code block.',
        'Do NOT add any comments in the SQL query.',
        `Error details:\n${errorContext}`,
        `SQL:\n${currentSql}`,
      ].join('\n\n'),
      terminalId: testTerminalRef.current || '',
      databaseType: databaseType,
      pageContext: {
        sqlContent: currentSql,
        validationError: errorContext,
      },
      options: {
        domainOverride: 'SQL_MIGRATION',
        modelName: autoFixModelRef.current,
        useLiveSchema: false,
        detailedComments: false,
      },
      conversationHistory: [],
    });
  };

  const handleCloseExplainPlan = () => {
    setValidationResult((prev: any) => prev ? { ...prev, planLines: [], syntaxErrors: [], vulnerabilities: [] } : null);
    setAiExplanation(null);
  };

  const handleExplainPlan = async () => {
    const currentFormData = formDataRef.current;
    if (!currentFormData.sqlContent.trim()) {
      showNotification('Please enter SQL content', 'warning');
      return;
    }

    // Convert parameters
    const params: Record<string, any> = {};
    testParametersRef.current.forEach((p) => {
      if (p.value) {
        if (p.type === 'NUMBER') {
          params[p.name] = Number(p.value);
        } else if (p.type === 'BOOLEAN') {
          params[p.name] = p.value.toLowerCase() === 'true';
        } else {
          params[p.name] = p.value;
        }
      }
    });

    try {
      setIsExplainingPlan(true);
      setValidationResult(null);
      setTestResults(null); 
      
      const validationResp = await queryService.validateQuery({
        sqlContent: currentFormData.sqlContent,
        terminalId: testTerminal,
        databaseType: databaseType,
        connectionId: databaseType === 'EXTERNAL' ? connectionId : undefined,
        parameters: params,
      });

      setValidationResult(validationResp);
      if (validationResp.isValid && validationResp.planLines && validationResp.planLines.length > 0) {
        showNotification('Execution Plan generated successfully', 'success');
      } else if (!validationResp.isValid) {
        showNotification('Validation failed for Explain Plan', 'error');
      } else {
        showNotification('Explain Plan generated but no lines were returned', 'warning');
      }
    } catch (e: any) {
      showNotification(`Explain Plan failed: ${e.message}`, 'error');
    } finally {
      setIsExplainingPlan(false);
    }
  };

  const testMutation = useMutation({
    mutationFn: (data: any) => queryService.executeRawSql({
      sqlContent: data.sqlContent,
      terminalId: data.terminalId,
      databaseType: data.databaseType,
      connectionId: data.connectionId,
      parameters: data.parameters,
    }),
    onSuccess: (response: any) => {
      setTestResults(response);
      if (response && response.status === 'SUCCESS') {
        showNotification(`Test successful! ${response.resultCount} rows returned.`, 'success');
      } else {
        showNotification(`Test failed: ${response?.errorMessage || 'Unknown error'}`, 'error');
      }
    },
    onError: (error: any) => {
      showNotification(`Test execution failed: ${error.message}`, 'error');
      setTestResults({
        status: 'ERROR',
        errorMessage: error.response?.data?.message || error.message,
      });
    },
  });

  // Input handlers
  const handleInputChange = (field: string, value: any) => {
    if (field === 'queryId') {
      return;
    }
    const finalValue = (typeof value === 'string' && field !== 'sqlContent') ? value.replace(/[<>]/g, '') : value;
    if (field === 'sqlContent' && aiCorrectionDiff && value !== aiCorrectionDiff.corrected) {
      setAiCorrectionDiff(null);
    }
    formDataRef.current = { ...formDataRef.current, [field]: finalValue };
    setFormData(prev => ({ ...prev, [field]: finalValue }));
  };

  const handleAddTag = (tagToAdd?: string | React.MouseEvent) => {
    const valToUse = (typeof tagToAdd === 'string') ? tagToAdd : currentTag;
    const sanitizedTag = sanitizeInput(valToUse);

    if (sanitizedTag && !tagsRef.current.includes(sanitizedTag)) {
      tagsRef.current = [...tagsRef.current, sanitizedTag];
      setTags(prev => [...prev, sanitizedTag]);
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    tagsRef.current = tagsRef.current.filter(tag => tag !== tagToRemove);
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };


  const handleTestParameterChange = (index: number, field: string, value: any) => {
    testParametersRef.current = testParametersRef.current.map((p, i) => {
      if (i === index) return { ...p, [field]: value };
      return p;
    });
    setTestParameters((prev) => {
      const newParams = [...prev];
      if (newParams[index]) {
        newParams[index] = { ...newParams[index], [field]: value };
      }
      return newParams;
    });
  }

  // Refs for stale closure prevention
  const formDataRef = React.useRef(formData);
  const parametersRef = React.useRef(parameters);
  const tagsRef = React.useRef(tags);
  const testParametersRef = React.useRef(testParameters);

  const handleExecuteQuery = async () => {
    const currentFormData = formDataRef.current;
    if (!currentFormData.sqlContent.trim()) {
      showNotification('Please enter SQL content', 'warning');
      return;
    }

    // Convert parameters
    const params: Record<string, any> = {};
    testParametersRef.current.forEach((p) => {
      if (p.value) {
        if (p.type === 'NUMBER') {
          params[p.name] = Number(p.value);
        } else if (p.type === 'BOOLEAN') {
          params[p.name] = p.value.toLowerCase() === 'true';
        } else {
          params[p.name] = p.value;
        }
      }
    });

    try {
      setValidationResult(null);
      setTestResults(null);
      
      // 1. Validate first (calling service directly to avoid triggering ExplainPlanCard in onExecute)
      const validationResp = await queryService.validateQuery({
        sqlContent: currentFormData.sqlContent,
        terminalId: testTerminal,
        databaseType: databaseType,
        connectionId: databaseType === 'EXTERNAL' ? connectionId : undefined,
        parameters: params,
      });

      // Set result but hide plan lines if it's a regular execution
      setValidationResult({
        ...validationResp,
        planLines: [] 
      });

      if (!validationResp.isValid) {
        return;
      }

      // 2. Then Execute
      await testMutation.mutateAsync({
        sqlContent: currentFormData.sqlContent,
        terminalId: testTerminal,
        databaseType: databaseType,
        connectionId: databaseType === 'EXTERNAL' ? connectionId : undefined,
        parameters: params,
      });
    } catch (e) {
      console.error('Execution failed', e);
    }
  };

  const handleSubmit = () => {
    if (updateQueryMutation.isPending) return;

    const currentFormData = formDataRef.current;
    const currentParameters = parametersRef.current;
    const currentTags = tagsRef.current;

    // Validation
    const isQueryIdValid = validateQueryId(currentFormData.queryId);
    if (!isQueryIdValid) {
      showNotification('Invalid Query ID: Must be 3-100 characters, alphanumeric, underscore, or hyphen only', 'error');
      return;
    }

    if (!currentFormData.name.trim()) {
      showNotification('Query name is required', 'error');
      return;
    }

    if (!currentFormData.description.trim()) {
      showNotification('Query description is required', 'error');
      return;
    }

    if (!currentFormData.category.trim()) {
      showNotification('Query category is required', 'error');
      return;
    }

    const sqlValidation = validateSQL(currentFormData.sqlContent);
    if (!sqlValidation.valid) {
      showNotification(`Invalid SQL: ${sqlValidation.error || 'Unknown validation error'}`, 'error');
      return;
    }

    // Validate parameters
    for (const param of currentParameters) {
      if (!param.name.trim()) {
        showNotification('All parameters must have a name', 'error');
        return;
      }
    }

    const queryData: Partial<QueryDefinition> = {
      queryId: currentFormData.queryId,
      name: currentFormData.name.trim(),
      description: currentFormData.description.trim(),
      category: currentFormData.category.trim(),
      applicationId: currentFormData.applicationId.trim(),
      sqlContent: currentFormData.sqlContent.trim(),
      tags: currentTags,
      parameters: currentParameters,
      metadata: currentFormData.notes.trim() ? { notes: currentFormData.notes.trim() } : undefined,
    };

    handleInputChange('makeCurrent', true); // Default to true when opening dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (updateQueryMutation.isPending) return;

    const currentFormData = formDataRef.current;
    const currentTags = tagsRef.current;

    const queryData: Partial<QueryDefinition> = {
      queryId: currentFormData.queryId,
      name: currentFormData.name.trim(),
      description: currentFormData.description.trim(),
      category: currentFormData.category.trim(),
      sqlContent: currentFormData.sqlContent.trim(),
      tags: currentTags,
      parameters: testParametersRef.current.map(p => ({
        name: p.name,
        type: p.type,
        defaultValue: p.value || '',
        description: p.description || '',
        required: true
      })),
      metadata: currentFormData.notes.trim() ? { notes: currentFormData.notes.trim() } : undefined,
      makeCurrent: currentFormData.makeCurrent,
      applicationId: currentFormData.applicationId || undefined,
      databaseType: databaseType || undefined,
      connectionId: (databaseType === 'EXTERNAL' && connectionId) ? connectionId : undefined,
    };

    setShowConfirmDialog(false);
    console.log('[QueryEditPage] Submitting update mutation with payload:', queryData);
    try {
      await updateQueryMutation.mutateAsync(queryData);
      await queryClient.invalidateQueries({ queryKey: ['query', queryId], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['query-versions', queryId], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['queries'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['terminalOverrides', queryId], refetchType: 'all' });
      navigate(`/queries/${queryId}`);
    } catch {
      // error notification handled by onError
    }
  };

  const handleCancel = () => {
    navigate(`/queries/${queryId}`);
  };

  if (isLoadingQuery) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column' }}>
        <McLoadingIndicator />
        <p className="mds-text--medium-regular" style={{ marginTop: '16px' }}>Loading query...</p>
      </div>
    );
  }

  if (queryError || !queryData) {
    return (
      <div style={{ padding: '24px' }}>
        <McNotification appearance="error" heading="Error" body={`Failed to load query: ${queryError?.message || 'Query not found'}`} />
      </div>
    );
  }

  return (
    <>
      {/* Main Content */}
      {activeStep === 0 && (
        <SqlStep
          sqlEditorRef={sqlEditorRef}
          onToggleSchemaPanel={toggleSchemaPanel}
          schemaPanelOpen={schemaPanelOpen}
          onOpenSchemaFullscreen={() => setShowSchemaExplorerFull(true)}
          onOpenSchemaPanelTab={openSchemaPanelTab}
          schemaPanelTab={schemaPanelTab}
          onInsertSnippet={(text) => {
            const editor = sqlEditorRef.current;
            if (!editor) {
              navigator.clipboard.writeText(text);
              showNotification(`Copied ${text}`, 'info');
              return;
            }
            const currentSql = isOverrideActive && terminalOverrideSql !== null ? terminalOverrideSql : formData.sqlContent;
            const sel = editor.getSelection();
            const prevChar = currentSql[sel.from - 1];
            const snippet = applyLeadingSpace(text, prevChar);
            editor.insertAtCursor(snippet);
            showNotification(`Inserted ${text}`, 'success');
          }}
          schemaExplorerWarning={schemaExplorerWarning}
          sqlContent={isOverrideActive && terminalOverrideSql !== null ? terminalOverrideSql : formData.sqlContent}
          onSqlChange={(val) => {
            if (isOverrideActive) {
              setTerminalOverrideSql(val);
            } else {
              handleInputChange('sqlContent', val);
            }
          }}
          testTerminal={testTerminal}
          onTestTerminalChange={(val) => {
            setTestTerminal(val);
            setSchemaExplorerWarning(null);
          }}
          testDatabaseType={databaseType}
          onTestDatabaseTypeChange={setDatabaseType}
          connectionId={connectionId}
          onConnectionChange={(id) => setConnectionId(id)}
          onExecute={handleExecuteQuery}
          isExecuting={validateQueryMutation.isPending || testMutation.isPending}
          validationResult={validationResult}
          testResults={testResults}
          testParameters={testParameters}
          onTestParameterChange={handleTestParameterChange}

          overrideBaseSql={isOverrideActive ? baseSqlRef.current : undefined}
          isOverride={isOverrideActive}
          onSaveOverride={() => { setOverrideMakeCurrent(true); setShowOverrideConfirmDialog(true); }}
          isSavingOverride={saveOverrideMutation.isPending}
          onDeleteOverride={() => deleteOverrideMutation.mutate()}
          isDeletingOverride={deleteOverrideMutation.isPending}

          onSave={handleSubmit}
          isSaving={updateQueryMutation.isPending}
          onBack={() => navigate(`/queries/${queryId}`)}
          onNext={() => setActiveStep(1)}

          isExplainingPlan={isExplainingPlan}
          aiExplanation={aiExplanation}
          isGeneratingAiExplanation={isGeneratingAiExplanation}
          onAiExplain={handleAiExplainPlan}
          onExplainPlan={handleExplainPlan}
          onCloseExplainPlan={handleCloseExplainPlan}
          onAiAssist={() => setAiPanelOpen(true)}
          isAiGenerating={aiGenerateMutation.isPending}
          onAutoFix={handleAutoFixSql}
          isAutoFixing={autoFixSqlMutation.isPending}
          autoFixModel={autoFixModel}
          onAutoFixModelChange={setAutoFixModel}
          autoFixModelOptions={aiModelOptions}
          unfixableReason={(() => { const c = classifySqlError(buildSqlErrorContext()); return c.fixable ? null : c.reason ?? null; })()}
          aiModelOptions={aiModelOptions}
          selectedModel={aiModelName}
          onModelChange={setAiModelName}
          aiCorrectionDiff={aiCorrectionDiff}
          onViewChanges={() => setShowDiffDialog(true)}
          onUndoCorrection={() => {
            if (aiCorrectionDiff) {
              handleInputChange('sqlContent', aiCorrectionDiff.original);
              setAiCorrectionDiff(null);
            }
          }}
        />
      )}

      {activeStep === 1 && (
        <InfoStep
          formData={formData}
          tags={tags}
          currentTag={currentTag}
          applications={applications}
          onInputChange={handleInputChange}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
          onCurrentTagChange={setCurrentTag}
          onNext={handleSubmit}
          onBack={() => setActiveStep(0)}
          isSaving={updateQueryMutation.isPending}
        />
      )}

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmSubmit}
        title="Confirm Query Update"
        content={
          <div>
            <p>Are you sure you want to save changes to this query?</p>
            <ul style={{ marginTop: '8px', listStyleType: 'disc', paddingLeft: '20px' }}>
              <li><strong>ID:</strong> {formData.queryId}</li>
              <li><strong>Name:</strong> {formData.name}</li>
            </ul>
            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--mds-color-neutral-weakest-background)', borderRadius: '8px', border: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.makeCurrent} 
                  onChange={(e) => handleInputChange('makeCurrent', e.target.checked)} 
                  style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span className="mds-text--medium-bold">Make this version current (Active in Production)?</span>
              </label>
              {!formData.makeCurrent && (
                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '12px', padding: '8px 12px', backgroundColor: 'var(--mds-color-warning-weakest-background, #fff8e1)', borderRadius: '6px', border: '1px solid var(--mds-color-warning-weak-decoration, #ffe082)' }}>
                  <McIcon icon="times-circle" style={{ color: 'var(--mds-color-warning-main-background, #f9a825)', marginRight: '8px', marginTop: '2px' }} />
                  <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-strong-text)' }}>
                    <strong>Warning:</strong> Unless it is marked as current, this query will be saved as a draft and will not be served in production.
                  </span>
                </div>
              )}
            </div>
          </div>
        }
        confirmLabel="Save Changes"
      />

      <ConfirmationDialog
        open={showOverrideConfirmDialog}
        onClose={() => setShowOverrideConfirmDialog(false)}
        onConfirm={() => {
          saveOverrideMutation.mutate(overrideMakeCurrent);
          setShowOverrideConfirmDialog(false);
        }}
        title={isOverrideActive ? `Update ⚡ ${testTerminal} override?` : `Save as ⚡ ${testTerminal} override?`}
        content={
          <div>
            <p>
              {isOverrideActive
                ? `This will save a new version of the ${testTerminal}-specific override.`
                : `This will save the current SQL as a terminal-specific override for ${testTerminal}.`}
            </p>
            <ul style={{ marginTop: '8px', listStyleType: 'disc', paddingLeft: '20px' }}>
              <li><strong>Query ID:</strong> {formData.queryId}</li>
              <li><strong>Terminal:</strong> {testTerminal}</li>
            </ul>
            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--mds-color-neutral-weakest-background)', borderRadius: '8px', border: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={overrideMakeCurrent}
                  onChange={(e) => setOverrideMakeCurrent(e.target.checked)}
                  style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span className="mds-text--medium-bold">Make this version current (Active in Production)?</span>
              </label>
              {!overrideMakeCurrent && (
                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '12px', padding: '8px 12px', backgroundColor: 'var(--mds-color-warning-weakest-background, #fff8e1)', borderRadius: '6px', border: '1px solid var(--mds-color-warning-weak-decoration, #ffe082)' }}>
                  <McIcon icon="times-circle" style={{ color: 'var(--mds-color-warning-main-background, #f9a825)', marginRight: '8px', marginTop: '2px' }} />
                  <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-strong-text)' }}>
                    <strong>Warning:</strong> This override will be saved as a draft and will not be served in production until marked as current.
                  </span>
                </div>
              )}
            </div>
          </div>
        }
        confirmLabel="Save Changes"
      />

      {/* Floating Assistant (AI chat) */}
      <FloatingAssistant
        open={aiPanelOpen}
        onOpen={() => setAiPanelOpen(true)}
        onClose={() => setAiPanelOpen(false)}
        hideLauncher={true}
        chatHistory={chatHistory}
        setChatHistory={setChatHistory}
        aiIntent={aiIntent}
        setAiIntent={setAiIntent}
        aiDomainOverride={aiDomainOverride}
        setAiDomainOverride={setAiDomainOverride}
        aiDomainOptions={aiDomainOptions}
        aiModelName={aiModelName}
        setAiModelName={setAiModelName}
        aiModelOptions={aiModelOptions}
        aiGenerateMutation={aiGenerateMutation}
        handleAiGenerate={handleAiGenerate}
        onRetryMessage={handleAiRetryMessage}
        chatScrollRef={chatScrollRef}
        selectedTerminal={testTerminal}
        setSelectedTerminal={setTestTerminal}
        databaseType={databaseType}
        setDatabaseType={setDatabaseType}
      />

      {aiCorrectionDiff && (
        <SqlDiffDialog
          open={showDiffDialog}
          onClose={() => setShowDiffDialog(false)}
          originalSql={aiCorrectionDiff.original}
          correctedSql={aiCorrectionDiff.corrected}
        />
      )}

      <SchemaExplorer
        open={showSchemaExplorerFull}
        onClose={() => setShowSchemaExplorerFull(false)}
        terminalId={testTerminal}
        databaseType={databaseType}
      />
    </>
  );
};

export default QueryEditPage;
